import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpHeaders,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { ApiPostOptions } from '../models/api.models';
import { LocalStorageStore } from '@localStore';
import { LOCAL_STORAGE_SELECTED_TENANTS_KEY } from '../../app/core/constants/tenants.constants';

/**
 * Service to manage API requests.
 * This service provides generic methods to interact with a RESTful API,
 * including GET, POST, PUT, and DELETE operations. It is designed to be
 * reusable and extensible for handling API requests throughout the application.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  /**
   * Injected instance of LocalStorageStore.
   * Used to retrieve data from local storage.
   */
  private localStore = inject(LocalStorageStore);

  /**
   * List of selected tenants retrieved from local storage.
   * This is used to manage tenant-specific API interactions.
   */
  private readonly selectedTenants: string[] = this.localStore.getItem(
    LOCAL_STORAGE_SELECTED_TENANTS_KEY,
  ) ?? [];

  /**
   * Base URL for the API.
   * Replace `API_URL` with the actual base URL of your backend API.
   */
  private readonly baseUrl = API_URL;

  /**
   * Injected instance of HttpClient.
   * Used to perform HTTP requests.
   */
  private readonly http = inject(HttpClient);

  /**
   * Perform a GET request to the API.
   *
   * @template T - The expected response type.
   * @param endpoint - The endpoint relative to the base URL.
   * @param params - Optional query parameters for the request.
   * @returns An Observable emitting the response of type `T`.
   */
  get<T>(endpoint: string, params?: HttpParams): Observable<T>;
  get<T>(endpoint: string, params: HttpParams, options: { headers?: { [key: string]: string } }): Observable<T>;
  get<T>(endpoint: string, params?: HttpParams, options?: { headers?: { [key: string]: string } }): Observable<T> {
    if (params) endpoint = this.replacePlaceholders(endpoint, params);

    const httpOptions: { params?: HttpParams; headers?: HttpHeaders } = { params };

    // If additional headers are provided, create HttpHeaders object
    // Angular HttpClient will automatically merge with interceptor headers
    if (options?.headers) {
      httpOptions.headers = new HttpHeaders(options.headers);
    }

    return this.http.get<T>(`${this.baseUrl}/${endpoint}`, httpOptions);
  }

  /**
   * Perform a POST request to the API and handle errors gracefully.
   *
   * @template T - The expected response type.
   * @param endpoint - The endpoint relative to the base URL.
   * @param body - The payload to be sent in the request body.
   * @param options - Optional HTTP options such as custom headers.
   * @returns An Observable emitting the response of type `T`, or an error if the request fails.
   */
  post<T>(endpoint: string, body: any, options?: ApiPostOptions): Observable<T> {
    if (!body) body = {}; // Ensure that body is not undefined or null

    const fullUrl = `${this.baseUrl}/${endpoint}`;

    try {
      return this.http
        .post<T>(fullUrl, body, options)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            // Rethrow the error or return a user-friendly message
            return throwError(
              () =>
                new Error(
                  error.message ||
                    'An error occurred while performing the POST request.',
                ),
            );
          }),
        );
    } catch (error) {
      // Handle any unexpected synchronous errors
      console.error('Unexpected error occurred:', error);
      throw new Error('An unexpected error occurred.');
    }
  }

  /**
   * Perform a PATCH request to the API and handle errors gracefully.
   *
   * @template T - The expected response type.
   * @param endpoint - The endpoint relative to the base URL.
   * @param body - The payload to be sent in the request body.
   * @param options - Optional HTTP options such as custom headers.
   * @returns An Observable emitting the response of type `T`, or an error if the request fails.
   */
  patch<T>(endpoint: string, body: any, options?: ApiPostOptions): Observable<T> {
    if (!body) body = {}; // Ensure that body is not undefined or null

    try {
      return this.http
        .patch<T>(`${this.baseUrl}/${endpoint}`, body, options)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            // Rethrow the error or return a user-friendly message
            return throwError(
              () =>
                new Error(
                  error.message ||
                    'An error occurred while performing the PATCH request.',
                ),
            );
          }),
        );
    } catch (error) {
      // Handle any unexpected synchronous errors
      console.error('Unexpected error occurred:', error);
      throw new Error('An unexpected error occurred.');
    }
  }

  /**
   * Perform a PUT request to the API.
   *
   * @template T - The expected response type.
   * @param endpoint - The endpoint relative to the base URL.
   * @param body - The payload to be sent in the request body.
   * @returns An Observable emitting the response of type `T`.
   */
  put<T>(endpoint: string, body: any): Observable<T> {
    if (!body) body = {}; // Ensure that body is not undefined or null
    return this.http.put<T>(`${this.baseUrl}/${endpoint}`, body);
  }

  /**
   * Perform a DELETE request to the API.
   *
   * @template T - The expected response type.
   * @param endpoint - The endpoint relative to the base URL.
   * @param body - The payload to be sent in the request body.
   * @returns An Observable emitting the response of type `T`.
   */
  delete<T>(endpoint: string, body: any): Observable<T> {
    if (!body) body = {}; // Ensure that body is not undefined or null
    return this.http.delete<T>(`${this.baseUrl}/${endpoint}`, { body });
  }

  /**
   * Perform a DELETE request to the API with custom headers.
   *
   * @template T - The expected response type.
   * @param endpoint - The endpoint relative to the base URL.
   * @param body - The payload to be sent in the request body.
   * @param headers - Custom headers to be sent with the request.
   * @returns An Observable emitting the response of type `T`.
   */
  deleteWithHeaders<T>(endpoint: string, body: any, headers: Record<string, string>): Observable<T> {
    if (!body) body = {}; // Ensure that body is not undefined or null
    return this.http.delete<T>(`${this.baseUrl}/${endpoint}`, {
      body,
      headers: new HttpHeaders(headers)
    });
  }

  /**
   * Replaces placeholders in the endpoint string with corresponding values from an object or HttpParams.
   *
   * @param template - The endpoint template string containing placeholders (e.g., `centers/{{centerId}}/transformers`).
   * @param params - Either a plain object or an HttpParams instance containing the key-value pairs.
   * @returns The endpoint string with placeholders replaced by actual values.
   */
  private replacePlaceholders(
    template: string,
    params: Record<string, any> | HttpParams,
  ): string {
    let paramsObject: Record<string, any>;

    // If params is an instance of HttpParams, convert it to an object
    if (params instanceof HttpParams) {
      paramsObject = {};
      params.keys().forEach((key) => {
        const value = params.get(key);
        if (value !== null) {
          paramsObject[key] = value;
        }
      });
    } else {
      paramsObject = { ...params };
    }

    // Replace placeholders in the template with actual values
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      if (!Object.prototype.hasOwnProperty.call(paramsObject, key)) {
        throw new Error(`Missing value for placeholder: ${key} on ${template}`);
      }
      const value = paramsObject[key];
      if (typeof value === 'undefined' || value === null) {
        throw new Error(`Value for placeholder "${key}" is invalid`);
      }
      return String(value); // Ensure the value is a string
    });
  }
}
